import React, { useCallback, useMemo, useRef } from 'react';
import { Box, Text } from '@common-fe/common-fe';
import _ from 'lodash';

import { ReportSummaryEmployerRow } from './ReportSummary.types';
import ReportSummaryBaseLoading from './ReportSummaryBaseLoading';
import ReportSummaryBaseSubsidiary from './ReportSummaryBaseSubsidiary';
import ReportSummaryBaseTable from './ReportSummaryBaseTable';

interface Props {
  isLoading?: boolean;
  isLoadingMore?: boolean;
  data: ReportSummaryEmployerRow[];
  isSmallMode?: boolean;
  subTitle?: string | null;
  isPreviewMode?: boolean;
  onLoadMore?: () => void;
  isHasMore?: boolean;
}

const ReportSummaryBase: React.FC<Props> = ({
  subTitle,
  data,
  isLoading,
  isLoadingMore,
  isSmallMode,
  isPreviewMode,
  onLoadMore,
  isHasMore,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!onLoadMore || !isHasMore || isLoadingMore) return;
    const element = scrollRef.current;
    if (!element) return;
    const { scrollTop, scrollHeight, clientHeight } = element;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      onLoadMore();
    }
  }, [onLoadMore, isHasMore, isLoadingMore]);
  const getItem = useCallback(
    (item: ReportSummaryEmployerRow, index = 0) => {
      if (item.rows && item.rows.length) {
        return item.rows.map((subsidiary, sIndex) => (
          <ReportSummaryBaseSubsidiary
            index={sIndex + 1}
            isPreviewMode={isPreviewMode}
            isLastItem={_.last(item.rows) === subsidiary}
            headers={item.headers}
            key={subsidiary.key}
            title={subsidiary.title}
            rows={subsidiary.rows}
            isTotalModeLastItem={subsidiary.isTotalModeLastItem}
          />
        ));
      }
      if (item.employerSummaryRows) {
        return (
          <ReportSummaryBaseTable
            isPreviewMode={isPreviewMode}
            subsidiaryIndex={index + 1}
            key={item.key}
            rows={item.employerSummaryRows}
            headers={item.headers}
          />
        );
      }
      return null;
    },
    [isPreviewMode]
  );

  const subTitleNode = useMemo(() => {
    if (subTitle && !isLoading) {
      return (
        <Text data-testid="report-summary_subtitle" color="textSecondary" size="medium" weight={400}>
          {subTitle}
        </Text>
      );
    }
    return null;
  }, [subTitle, isLoading]);
  return (
    <div>
      <Box
        fill
        direction="column"
        round="container1Round"
        border={{ size: 'small', color: 'border1' }}
        background="canvas"
        pad="spacing18"
        data-testid="report-summary"
        overflow="auto"
        style={{
          maxWidth: isSmallMode && !isPreviewMode ? '520px' : undefined,

          maxHeight: isPreviewMode ? '450px' : undefined,
        }}
      >
        <Box direction="row" justify="between" align="center">
          <Text size="xxlarge" style={{ lineHeight: '28px' }} weight={700}>
            Summary
          </Text>
          {!isPreviewMode && subTitleNode ? subTitleNode : null}
        </Box>
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          direction="column"
          overflow="auto"
          height={{ max: 'calc(100vh - 420px)' }}
          style={{
            position: 'relative',
          }}
        >
          {isLoading ? (
            <ReportSummaryBaseLoading isSmallMode={isSmallMode} />
          ) : (
            <div>
              <>
                {data.map((item, index) => (
                  <Box key={item.key}>
                    <Box
                      background="canvas"
                      style={
                        isPreviewMode
                          ? undefined
                          : {
                              top: -18,
                              position: 'sticky',
                            }
                      }
                    >
                      <Text
                        data-testid={`report-summary_title_${index + 1}`}
                        margin={{ vertical: 'spacing8' }}
                        weight={700}
                      >
                        {item.title}
                      </Text>
                    </Box>
                    {getItem(item, index)}
                  </Box>
                ))}
              </>
              {isLoadingMore && (
                <Box fill>
                  <ReportSummaryBaseLoading isSmallMode={isSmallMode} />
                </Box>
              )}
            </div>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default ReportSummaryBase;
